import {
  BasketAdvisorOrderEntity,
  BasketCustomerOrderEntity,
  BasketMarketOfferEntity,
} from '@genial/services';
import { Utils } from '@genial/utils';

import { AssetsMarketTypeEnum } from '../types';

import {
  batchTicketOrderResumePdfTemplateProps,
  coesOrderResumeData,
  directTreasuryOrderResumeData,
  equitiesOrderResumeData,
  fixedIncomeOrderResumeData,
  fundsOrderResumeData,
} from './types';

const errorMessage = '--';

const getFilterBasketCustomer = (
  basketMarketData: BasketMarketOfferEntity[],
  marketType: string
) => {
  return basketMarketData.find((item) => item.marketType === marketType);
};

const getCoesData = (basketMarketData: BasketMarketOfferEntity[]): coesOrderResumeData[] => {
  const coesFiltered = getFilterBasketCustomer(basketMarketData, AssetsMarketTypeEnum.Coe);

  if (!coesFiltered) {
    return [];
  }

  const assets = coesFiltered.basketOffers || [];

  return assets.map((item) => ({
    productDisplay: item.offer.productDisplay || errorMessage,
    strategy: item.offer.strategy || errorMessage,
    maturityDate: item.offer.maturityDate
      ? Utils.Mask.DateFormat(item.offer.maturityDate, 'dd/MM/yyyy')
      : errorMessage,
    riskRating: item.offer?.suitabilityProductRisk?.risk || errorMessage,
    minApplicationValue: item.offer.minimumApplication || 0,
    minApplicationValueFormatted: Utils.Mask.Money(item.offer.minimumApplication),
    applicationIn: item.offer.applicationIn || errorMessage,
    valueApplied: item.requestedAmount,
    valueAppliedFormatted: Utils.Mask.Money(item.requestedAmount),
  }));
};

const getDirectTreasuryData = (
  basketMarketData: BasketMarketOfferEntity[]
): directTreasuryOrderResumeData[] => {
  const directTreasuryFiltered = getFilterBasketCustomer(
    basketMarketData,
    AssetsMarketTypeEnum.Treasury
  );

  if (!directTreasuryFiltered) {
    return [];
  }

  const assets = directTreasuryFiltered.basketOffers || [];

  return assets.map((item) => ({
    productDisplay: item.offer.productDisplay || errorMessage,
    applicationDate: Utils.Mask.DateFormat(new Date(), 'dd/MM/yyyy'),
    liquidityDate: item.offer.settlementDate
      ? Utils.Mask.DateFormat(item.offer.settlementDate, 'dd/MM/yyyy')
      : errorMessage,
    maturityDate: item.offer.maturityDate
      ? Utils.Mask.DateFormat(item.offer.maturityDate, 'dd/MM/yyyy')
      : errorMessage,
    quantity: `${item.quantity}`,
    valueAppliedFormatted: Utils.Mask.Money(item.requestedAmount),
    valueApplied: item.requestedAmount,
  }));
};

const getFixedIncomeData = (
  basketMarketData: BasketMarketOfferEntity[]
): fixedIncomeOrderResumeData[] => {
  const fixedIncomeFiltered = getFilterBasketCustomer(
    basketMarketData,
    AssetsMarketTypeEnum.FixedIncome
  );

  if (!fixedIncomeFiltered) {
    return [];
  }

  const assets = fixedIncomeFiltered.basketOffers || [];

  return assets.map((item) => ({
    productDisplay: item.offer.productDisplay || errorMessage,
    riskRating: item.offer.riskRatingName || errorMessage,
    maturityDate: item.offer.maturityDate
      ? Utils.Mask.DateFormat(item.offer.maturityDate, 'dd/MM/yyyy')
      : errorMessage,
    profitability: item.offer.profitabilityFormatted || errorMessage,
    liquidity: item.offer.liquidityIndicator || errorMessage,
    feesPayment: item.offer.feesPayment || errorMessage,
    valueAppliedFormatted: Utils.Mask.Money(item.requestedAmount),
    valueApplied: item.requestedAmount,
  }));
};

const getEquitiesData = (
  basketMarketData: BasketMarketOfferEntity[]
): equitiesOrderResumeData[] => {
  const equitiesFiltered = getFilterBasketCustomer(basketMarketData, AssetsMarketTypeEnum.Equity);

  if (!equitiesFiltered) {
    return [];
  }

  const formatLastPricePercentage = (percentage?: number | null) => {
    if (!percentage || percentage === 0) {
      return '';
    }

    const percentageFormatted = Utils.Mask.Percentage(percentage);

    const operator = percentage > 0 ? '+' : '-';

    return `${operator}${percentageFormatted}`;
  };

  const assets = equitiesFiltered.basketOffers || [];

  return assets.map((item) => ({
    productDisplay: item.offer.productDisplay || errorMessage,
    operation: 'Compra',
    lastPriceValue: item.offer.lastPrice || 0,
    lastPriceValueFormatted: Utils.Mask.Money(item.offer.lastPrice),
    priceType: 'Limitada',
    quantity: `${item.requestedQuantity}`,
    valueAppliedFormatted: Utils.Mask.Money(item.requestedPrice),
    totalValueAppliedFormatted: Utils.Mask.Money(item.requestedAmount),
    assetPrice: item.offer.closePrice || 0,
    assetPriceFormatted: Utils.Mask.Money(item.offer.closePrice),
    lastPriceDate: item.offer.lastUpdatedAt
      ? `Atualizado às ${Utils.Mask.DateFormat(item.offer.lastUpdatedAt, 'HH:mm')}`
      : errorMessage,
    lastPricePercentage: item.offer.priceChangeTax || 0,
    lastPricePercentageFormatted: formatLastPricePercentage(item.offer.priceChangeTax),
    maturityDate: `Hoje (${Utils.Mask.DateFormat(new Date(), 'dd/MM/yyyy')})`,
    valueApplied: item.requestedPrice,
    totalValueApplied: item.requestedAmount,
  }));
};

const getFundsData = (basketMarketData: BasketMarketOfferEntity[]): fundsOrderResumeData[] => {
  const fundsFiltered = getFilterBasketCustomer(basketMarketData, AssetsMarketTypeEnum.Fund);

  if (!fundsFiltered) {
    return [];
  }

  const assets = fundsFiltered.basketOffers || [];

  return assets.map((item) => ({
    productDisplay: item.offer.productDisplay || errorMessage,
    riskRating: item.offer.riskRating || errorMessage,
    profitabilityTwelveMonths: item.offer.profitabilityTwelveMonthsAdjusted
      ? Utils.Mask.Percentage(item.offer.profitabilityTwelveMonthsAdjusted)
      : errorMessage,
    adminTax: item.offer.administrationFee || errorMessage,
    investmentRedemption: item.offer.redemptionDescription || errorMessage,
    valueAppliedFormatted: Utils.Mask.Money(item.requestedAmount),
    valueApplied: item.requestedAmount,
  }));
};

const getMarketTotalAmount = (basketMarketData: BasketMarketOfferEntity[], marketType: string) => {
  const basketFiltered = getFilterBasketCustomer(basketMarketData, marketType);

  if (!basketFiltered) {
    return 0;
  }

  return basketFiltered.totalMarketRequestedAmount || 0;
};

export const normalizeBatchTicketInfo = (
  batchTicketInfo: BasketCustomerOrderEntity,
  basketData: BasketAdvisorOrderEntity
): batchTicketOrderResumePdfTemplateProps => {
  const sendDate = basketData.updateDate;

  const orderTotalValue = basketData.totalRequestedAmount || 0;

  const customerOffers = batchTicketInfo.customerOffers || [];

  const coesDataOffers = getCoesData(customerOffers);
  const directTreasuryDataOffers = getDirectTreasuryData(customerOffers);
  const fixedIncomeDataOffers = getFixedIncomeData(customerOffers);
  const equitiesDataOffers = getEquitiesData(customerOffers);
  const fundsDataOffers = getFundsData(customerOffers);

  const coesTotalValue = getMarketTotalAmount(customerOffers, AssetsMarketTypeEnum.Coe);
  const directTreasuryTotalValue = getMarketTotalAmount(
    customerOffers,
    AssetsMarketTypeEnum.Treasury
  );
  const fixedIncomeTotalValue = getMarketTotalAmount(
    customerOffers,
    AssetsMarketTypeEnum.FixedIncome
  );
  const equitiesTotalValue = getMarketTotalAmount(customerOffers, AssetsMarketTypeEnum.Equity);
  const fundsTotalValue = getMarketTotalAmount(customerOffers, AssetsMarketTypeEnum.Fund);

  return {
    orderName: `Cesta - ${basketData.basketOrderHash}`,
    sendDate: sendDate ? Utils.Mask.DateFormat(sendDate, 'dd/MM/yyyy') : errorMessage,
    totalValue: Utils.Mask.Money(orderTotalValue),
    coesData: {
      totalValue: Utils.Mask.Money(coesTotalValue),
      offers: coesDataOffers,
    },
    directTreasuryData: {
      totalValue: Utils.Mask.Money(directTreasuryTotalValue),
      offers: directTreasuryDataOffers,
    },
    fixedIncomeData: {
      totalValue: Utils.Mask.Money(fixedIncomeTotalValue),
      offers: fixedIncomeDataOffers,
    },
    equitiesData: {
      totalValue: Utils.Mask.Money(equitiesTotalValue),
      offers: equitiesDataOffers,
    },
    fundsData: {
      totalValue: Utils.Mask.Money(fundsTotalValue),
      offers: fundsDataOffers,
    },
  };
};
